import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { BrandingProvider } from "@/lib/branding";
import { PortalAuthProvider } from "@/lib/portalAuth";
import { isPortalHost } from "@/lib/hostRouting";
import { installToastSounds } from "@/lib/notifySound";
import Protected from "@/components/Protected";
import PortalLayout from "@/components/PortalLayout";
import Login from "@/pages/Login";
import Settings from "@/pages/Settings";
import TestCertificates from "@/pages/TestCertificates";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Leads from "@/pages/Leads";
import Quotations from "@/pages/Quotations";
import Proformas from "@/pages/Proformas";
import Products from "@/pages/Products";
import SalesOrders from "@/pages/SalesOrders";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Transporters from "@/pages/Transporters";
import Vendors from "@/pages/Vendors";
import TrackShipment from "@/pages/TrackShipment";
import Production from "@/pages/Production";
import Consignments from "@/pages/Consignments";
import Dispatch from "@/pages/Dispatch";
import Invoices from "@/pages/Invoices";
import Payments from "@/pages/Payments";
import VendorBillsPage from "@/pages/VendorBillsPage";
import Reports from "@/pages/Reports";
import Disk from "@/pages/Disk";
import Notifications from "@/pages/Notifications";
import RateFinder from "@/pages/RateFinder";
import SupportTickets from "@/pages/SupportTickets";
import FilesMedia from "@/pages/FilesMedia";
import SyncCenter from "@/pages/SyncCenter";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalQuotations from "@/pages/portal/PortalQuotations";
import PortalOrders from "@/pages/portal/PortalOrders";
import PortalDispatch from "@/pages/portal/PortalDispatch";
import PortalLedger from "@/pages/portal/PortalLedger";
import PortalInvoices from "@/pages/portal/PortalInvoices";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import PortalCatalogue from "@/pages/portal/PortalCatalogue";
import PortalSupport from "@/pages/portal/PortalSupport";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CatalogueAdmin from "@/pages/CatalogueAdmin";
import SizeIntelligence from "@/pages/SizeIntelligence";
import Inventory from "@/pages/Inventory";

const P = ({ children }) => <PortalLayout>{children}</PortalLayout>;

// Bare-domain entry point: on portal.* the root shows the client app (which
// redirects to /portal/login when unauthenticated); everywhere else it shows
// the staff CMS (which redirects to /login when unauthenticated).
const RootEntry = () =>
  isPortalHost() ? <Navigate to="/portal" replace /> : <Protected><Dashboard /></Protected>;

function App() {
  installToastSounds();
  return (
    <AuthProvider>
      <BrandingProvider>
      <PortalAuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/track/:token" element={<TrackShipment />} />
          <Route path="/portal/login" element={<PortalLogin />} />
          {/* Friendly client-facing aliases: cms.hitechsilico.in/client/login */}
          <Route path="/client/login" element={<Navigate to="/portal/login" replace />} />
          <Route path="/client" element={<Navigate to="/portal" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/portal/forgot-password" element={<ForgotPassword portal />} />
          <Route path="/portal/reset-password" element={<ResetPassword portal />} />
          <Route path="/portal" element={<P><PortalDashboard /></P>} />
          <Route path="/portal/quotations" element={<P><PortalQuotations /></P>} />
          <Route path="/portal/orders" element={<P><PortalOrders /></P>} />
          <Route path="/portal/dispatch" element={<P><PortalDispatch /></P>} />
          <Route path="/portal/ledger" element={<P><PortalLedger /></P>} />
          <Route path="/portal/invoices" element={<P><PortalInvoices /></P>} />
          <Route path="/portal/documents" element={<P><PortalDocuments /></P>} />
          <Route path="/portal/catalogue" element={<P><PortalCatalogue /></P>} />
          <Route path="/portal/support" element={<P><PortalSupport /></P>} />
          <Route path="/" element={<RootEntry />} />
          <Route path="/customers" element={<Protected><Customers /></Protected>} />
          <Route path="/leads" element={<Protected><Leads /></Protected>} />
          <Route path="/quotations" element={<Protected><Quotations /></Protected>} />
          <Route path="/proformas" element={<Protected><Proformas /></Protected>} />
          <Route path="/products" element={<Protected><Products /></Protected>} />
          <Route path="/catalogue" element={<Protected><CatalogueAdmin /></Protected>} />
          <Route path="/size-intelligence" element={<Protected><SizeIntelligence /></Protected>} />
          <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
          <Route path="/orders" element={<Protected><SalesOrders /></Protected>} />
          <Route path="/purchase-orders" element={<Protected><PurchaseOrders /></Protected>} />
          <Route path="/transporters" element={<Protected><Transporters /></Protected>} />
          <Route path="/vendors" element={<Protected><Vendors /></Protected>} />
          <Route path="/production" element={<Protected><Production /></Protected>} />
          <Route path="/dispatch" element={<Protected><Dispatch /></Protected>} />
          <Route path="/consignments" element={<Protected><Consignments /></Protected>} />
          <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
          <Route path="/payments" element={<Protected><Payments /></Protected>} />
          <Route path="/payments/payables" element={<Protected><Payments section="payables" /></Protected>} />
          <Route path="/payments/payables/vendor/:vid" element={<Protected><VendorBillsPage /></Protected>} />
          <Route path="/reports" element={<Protected><Reports /></Protected>} />
          <Route path="/disk" element={<Protected><Disk /></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/sync" element={<Protected><SyncCenter /></Protected>} />
          <Route path="/test-certificates" element={<Protected><TestCertificates /></Protected>} />
          <Route path="/rate-finder" element={<Protected><RateFinder /></Protected>} />
          <Route path="/support-tickets" element={<Protected><SupportTickets /></Protected>} />
          <Route path="/files" element={<Protected><FilesMedia /></Protected>} />
        </Routes>
      </BrowserRouter>
      </PortalAuthProvider>
      </BrandingProvider>
    </AuthProvider>
  );
}

export default App;
